import { FC, useCallback } from 'react'

import { utils } from 'ethers'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'

import { styled } from '@mui/material/styles'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container, { ContainerProps } from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import Back from '@mui/icons-material/ArrowBack'

import useDApp from 'contexts/Web3'

import Help from 'components/Help'
import UglySnackbar from 'components/UglySnackbar'

import { LinkHome } from 'routes'
import isSameAddress from 'utils/isSameAddress'
import shortenAccount from 'utils/shortenAccount'
import useContractInteraction from 'hooks/useContractInteraction'

/* eslint-disable-next-line import/no-webpack-loader-syntax */
import imgHelp from '!!file-loader!assets/help-cashback.png'
import './cashback.scss'

const CleanCell = styled(TableCell)({
  borderBottom: 'none',
})

const LinedCell = styled(TableCell)({
  borderBottom: '0.5px solid #9B9B9B',
})

interface CashbackProps extends ContainerProps {}

const CashbackContainer: FC<CashbackProps> = () => {
  const {
    gladiator: { champions, claimables, weekPlayed, total, lastSeen, nextRound },
    actions,
    wallet: { account },
  } = useDApp()

  const claimState = useContractInteraction()

  const hasReward = Number(claimables) > 0

  const canClaim = hasReward && claimState.state === 'idle'

  const onClaim = useCallback(() => {
    claimState.start()

    actions
      .claim()
      .then(claimState.onDone)
      .catch(claimState.onError)
      .finally(actions.refreshGameInfo)
  }, [actions, claimState])

  return (
    <Container
      className="cashback-view"
      maxWidth="sm"
      disableGutters
    >
      <Stack direction="row" alignItems="center" p={1}>
        <LinkHome>
          <Typography variant="HeaderUI" flexGrow={0}>
            <IconButton color="inherit">
              <Back color="inherit" fontSize="large" />
            </IconButton>
          </Typography>
        </LinkHome>

        <Box flexGrow={1} />

        <Typography variant="HeaderUI" flexGrow={0}>
          <Help image={imgHelp} />
        </Typography>
      </Stack>

      <Box height={80} />

      <Box p={2} width="100%">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <CleanCell align="left" colSpan={2}>
                  <Typography variant="CashbackTableHeader">TOP 3</Typography>
                </CleanCell>
                <CleanCell align="center" width="35%">
                  <Typography variant="CashbackTableHeader">
                    Accumulated
                  </Typography>
                </CleanCell>
                <CleanCell align="center">
                  <Typography variant="CashbackTableHeader">
                    Cashback Ratio
                  </Typography>
                </CleanCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {champions.map(({ address, ratio, weekPlayed }, i) => (
                <TableRow
                  key={i}
                  selected={Boolean(account && isSameAddress(account, address))}
                >
                  <LinedCell width="10%" component="th" scope="row">
                    <Typography variant="CashbackTableHeader">
                      {i + 1}
                    </Typography>
                  </LinedCell>
                  <LinedCell width="30%" component="td" scope="row">
                    {
                      address && account && isSameAddress(account, address)
                        ? (
                          <Badge badgeContent="🎉">
                            <Typography variant="CashbackTableContent">
                              {shortenAccount(address as string)}
                            </Typography>
                          </Badge>
                        )
                        : (
                          address
                            ? (
                              <Typography variant="CashbackTableContent">
                                {shortenAccount(address)}
                              </Typography>
                            )
                            : (
                              <Typography variant="CashbackTableContent">
                                (None)
                              </Typography>
                            )
                        )
                    }
                  </LinedCell>
                  <LinedCell width="40%" align="center">
                    {weekPlayed ? (
                      <Typography variant="CashbackTableContent">
                        {utils.commify(weekPlayed.toFixed(1))}
                      </Typography>
                    ) : (
                      <Typography variant="CashbackTableContent">--</Typography>
                    )}
                  </LinedCell>
                  <LinedCell width="20%" align="center">
                    {ratio ? (
                      <Typography variant="CashbackTableContent">
                        {(ratio * 100).toFixed(1)}%
                      </Typography>
                    ) : null}
                  </LinedCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box p={1} width="100%">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <CleanCell colSpan={2}>
                  <Typography variant="CashbackStatisticsHeader">
                    My Statistics
                  </Typography>
                </CleanCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow>
                <CleanCell width="50%">
                  <Typography variant="CashbackStatisticsSubject">
                    Last played
                  </Typography>
                </CleanCell>
                <CleanCell width="40%" align="right">
                  <Typography variant="CashbackStatisticsContent">
                    {lastSeen ? `${formatDistanceToNow(lastSeen)} ago` : 'never'}
                  </Typography>
                </CleanCell>
              </TableRow>
              <TableRow>
                <CleanCell width="50%">
                  <Typography variant="CashbackStatisticsSubject">
                    Played this round
                  </Typography>
                </CleanCell>
                <CleanCell width="50%" align="right">
                  <Typography variant="CashbackStatisticsContent">
                    $ {weekPlayed} TT
                  </Typography>
                </CleanCell>
              </TableRow>
              <TableRow>
                <CleanCell width="50%">
                  <Typography variant="CashbackStatisticsSubject">
                    This round remains
                  </Typography>
                </CleanCell>
                <CleanCell width="50%" align="right">
                  <Typography variant="CashbackStatisticsContent">
                    { nextRound ? formatDistanceToNow(nextRound) : '---' }
                  </Typography>
                </CleanCell>
              </TableRow>
              <TableRow>
                <CleanCell width="50%">
                  <Typography variant="CashbackStatisticsSubject">
                    Total played
                  </Typography>
                </CleanCell>
                <CleanCell width="50%" align="right">
                  <Typography variant="CashbackStatisticsContent">
                    $ {total} TT
                  </Typography>
                </CleanCell>
              </TableRow>
              <TableRow>
                <CleanCell width="50%">
                  <Typography variant="CashbackStatisticsSubject">
                    My cashback rewards
                  </Typography>
                </CleanCell>
                <CleanCell width="50%" align="right">
                  <Typography variant="CashbackStatisticsContent">
                    $ {claimables} TT
                  </Typography>
                </CleanCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box mt={4} width="100%" textAlign="center">
        <Button
          variant="ugly"
          color="primary"
          disabled={!canClaim}
          onClick={onClaim}
        >
          Claim rewards
        </Button>
      </Box>

      <UglySnackbar
        open={claimState.state !== 'idle'}
        onClose={claimState.reset}
        state={claimState.state}
      >
        <>{ claimState.error ?? 'Claiming...' }</>
      </UglySnackbar>
    </Container>
  )
}

export default CashbackContainer
